import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  IconButton,
  TextField,
  Button,
  Collapse,
  CircularProgress
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  Edit,
  CheckCircle,
  Cancel,
  Refresh,
  AttachMoney
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { confirmIncome, recalculateIncome } from '../services/incomeApi';
import { colors } from '../styles';

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: '20px',
  backgroundColor: colors.white,
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  transition: 'all 0.2s ease-in-out',
  marginBottom: theme.spacing(3),
  border: `2px solid ${colors.warning}`,
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0,0,0,0.12)'
  }
}));

const SourceCard = styled(Box)(({ theme, confirmed, rejected }) => ({
  borderRadius: '12px',
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  border: `1px solid ${
    confirmed ? colors.success : 
    rejected ? colors.error : 
    colors.border
  }`,
  backgroundColor: confirmed ? `${colors.success}08` : 
                   rejected ? `${colors.error}05` : 
                   colors.white,
  transition: 'all 0.2s ease-in-out'
}));

const FrequencyChip = styled(Chip)(({ theme }) => ({
  backgroundColor: `${colors.primary}15`,
  color: colors.primary,
  fontWeight: 600,
  fontSize: '13px',
  height: 28
}));

const IncomeConfirmationCard = ({ incomeData, onUpdate, onError }) => {
  const [expanded, setExpanded] = useState(true);
  const [sources, setSources] = useState(incomeData?.sources || []);
  const [editingSource, setEditingSource] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recalculating, setRecalculating] = useState(false);

  React.useEffect(() => {
    if (incomeData?.sources) {
      setSources([...incomeData.sources]);
    }
  }, [incomeData]);

  const handleConfirmSource = (key) => {
    setSources(prev => prev.map(s =>
      s.key === key ? { ...s, confirmed: true } : s
    ));
  };

  const handleRejectSource = (key) => {
    setSources(prev => prev.map(s =>
      s.key === key ? { ...s, confirmed: false } : s
    ));
  };

  const handleRenameSource = (key, newName) => {
    setSources(prev => prev.map(s =>
      s.key === key ? { ...s, displayName: newName } : s
    ));
    setEditingSource(null);
  };

  const handleConfirmAll = async () => {
    try {
      setLoading(true);
      const sourcesToSend = sources.map(s => ({
        key: s.key,
        displayName: s.displayName,
        confirmed: s.confirmed ?? true
      }));

      const response = await confirmIncome(sourcesToSend);
      onUpdate(response.data.income);
    } catch (error) {
      console.error('Error confirming income:', error);
      onError(error?.response?.data?.error || 'Failed to confirm income');
    } finally {
      setLoading(false);
    }
  };

  const handleRecalculate = async () => {
    try {
      setRecalculating(true);
      const response = await recalculateIncome();
      const body = response?.data || {};
      // pending:true → cached snapshot while background job runs (#1307 / #1312)
      if (body.income?.sources) {
        setSources(body.income.sources);
      }
      if (body.income && onUpdate) {
        onUpdate(body.income, {
          kind: 'recalculate',
          pending: body.pending === true,
          message: body.message,
        });
      }
    } catch (error) {
      console.error('Error recalculating income:', error);
      onError(error?.response?.data?.error || 'Failed to recalculate income');
    } finally {
      setRecalculating(false);
    }
  };

  const getFrequencyLabel = (frequency) => {
    if (!frequency) return 'Unknown';
    return frequency.charAt(0).toUpperCase() + frequency.slice(1);
  };

  const getTotalMonthly = () => {
    return sources
      .filter(s => s.confirmed !== false)
      .reduce((sum, s) => sum + s.monthlyAmount, 0);
  };

  if (!incomeData || !incomeData.needsReview) return null;

  return (
    <StyledCard>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <AttachMoney sx={{ color: colors.success, fontSize: 32 }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: colors.text.primary }}>
                Confirm Your Income
              </Typography>
              <Typography variant="body2" color="text.secondary">
                We detected {sources.length} income source{sources.length > 1 ? 's' : ''} from your transactions
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton
              onClick={handleRecalculate}
              disabled={recalculating}
              size="small"
            >
              {recalculating ? (
                <CircularProgress size={24} />
              ) : (
                <Refresh />
              )}
            </IconButton>
            <IconButton
              onClick={() => setExpanded(!expanded)}
              size="small"
            >
              {expanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
        </Box>

        <Collapse in={expanded}>
          <Box sx={{ mt: 2 }}>
            {sources.map((source) => (
              <SourceCard
                key={source.key}
                confirmed={source.confirmed}
                rejected={source.confirmed === false}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                  <Box sx={{ flex: 1 }}>
                    {editingSource === source.key ? (
                      <TextField
                        value={source.displayName}
                        onChange={(e) => handleRenameSource(source.key, e.target.value)}
                        onBlur={() => setEditingSource(null)}
                        autoFocus
                        size="small"
                        fullWidth
                        sx={{ mb: 1 }}
                      />
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                          {source.displayName}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => setEditingSource(source.key)}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                    {source.frequency && (
                      <FrequencyChip
                        label={getFrequencyLabel(source.frequency)}
                        size="small"
                      />
                    )}
                  </Box>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      Monthly:
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      ${source.monthlyAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                  </Box>
                  {source.frequency && source.frequency !== 'irregular' && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        Per {source.frequency}:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        ${source.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Typography>
                    </Box>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    Based on {source.transactionCount} transaction{source.transactionCount !== 1 ? 's' : ''}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant={source.confirmed ? "contained" : "outlined"}
                    size="small"
                    onClick={() => handleConfirmSource(source.key)}
                    startIcon={<CheckCircle />}
                    sx={{
                      flex: 1,
                      borderRadius: '8px',
                      ...(source.confirmed && {
                        backgroundColor: colors.success,
                        '&:hover': { backgroundColor: colors.success }
                      })
                    }}
                  >
                    {source.confirmed ? 'Confirmed' : 'Confirm'}
                  </Button>
                  <Button
                    variant={source.confirmed === false ? "contained" : "outlined"}
                    size="small"
                    onClick={() => handleRejectSource(source.key)}
                    startIcon={<Cancel />}
                    color="error"
                    sx={{
                      flex: 1,
                      borderRadius: '8px'
                    }}
                  >
                    {source.confirmed === false ? 'Rejected' : 'Reject'}
                  </Button>
                </Box>
              </SourceCard>
            ))}

            {/* Total Summary */}
            <Box sx={{
              backgroundColor: `${colors.primary}10`,
              borderRadius: '12px',
              padding: 3,
              textAlign: 'center',
              mt: 2,
              mb: 3
            }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Estimated Monthly Income
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: colors.primary, mb: 0.5 }}>
                ${getTotalMonthly().toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Based on confirmed sources only
              </Typography>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                fullWidth
                onClick={handleConfirmAll}
                disabled={loading || recalculating}
                startIcon={loading ? <CircularProgress size={20} /> : <CheckCircle />}
                sx={{
                  backgroundColor: colors.primary,
                  borderRadius: '999px',
                  py: 1.5,
                  fontWeight: 700,
                  '&:hover': {
                    backgroundColor: colors.primary
                  }
                }}
              >
                {loading ? 'Confirming...' : 'Looks Good'}
              </Button>
            </Box>
          </Box>
        </Collapse>
      </CardContent>
    </StyledCard>
  );
};

export default IncomeConfirmationCard;

